/**
 * LinkedInConnect — manage the user's LinkedIn account connection.
 *
 * Initiates a Unipile hosted-auth OAuth popup and polls for connection status.
 */

import { useState, useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Linkedin, CheckCircle2, AlertTriangle, Loader2, Info } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useApi } from '@/hooks/use-api'
import type { ApiFetch } from '@/lib/api'
import { parseApiDate } from '@/lib/dates'

// ============================================================
// API helpers
// ============================================================

type ConnectApiResponse = {
  readonly success: boolean
  readonly auth_url?: string
  readonly error?: string
}

type StatusResponse = {
  readonly success: boolean
  readonly status: 'not_connected' | 'connected' | 'reconnect_needed'
  readonly account: {
    readonly name: string
    readonly connected_at: string | null
    readonly unipile_account_id: string
  } | null
}

async function fetchLinkedInStatus(apiFetch: ApiFetch): Promise<StatusResponse> {
  const res = await apiFetch<StatusResponse>('/api/leads/linkedin/status')
  if (!res.success || !res.data) throw new Error(res.error ?? 'Status check failed')
  return res.data
}

async function initiateLinkedInConnect(apiFetch: ApiFetch): Promise<ConnectApiResponse> {
  const res = await apiFetch<ConnectApiResponse>('/api/leads/linkedin/connect', {
    method: 'POST',
    body: {},
  })
  if (!res.success || !res.data) return { success: false, error: res.error ?? 'Connection failed' }
  // res.data contains { auth_url } from the envelope's data field; success is in the outer envelope.
  return { success: true, auth_url: res.data.auth_url }
}

async function performLinkedInDisconnect(apiFetch: ApiFetch): Promise<{ success: boolean; error?: string }> {
  const res = await apiFetch<{ status: string }>('/api/leads/linkedin/disconnect', {
    method: 'POST',
    body: {},
  })
  if (!res.success) return { success: false, error: res.error ?? 'Disconnect failed' }
  return { success: true }
}

// ============================================================
// Types
// ============================================================

type ConnectionStatus = 'not_connected' | 'connected' | 'reconnect_needed'

type ConnectedAccount = {
  readonly name: string
  readonly connectedAt: Date
}

// ============================================================
// Helpers
// ============================================================

function formatDate(d: Date): string {
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
}

// ============================================================
// Status badge
// ============================================================

function StatusBadge({ status }: { readonly status: ConnectionStatus }) {
  if (status === 'connected') {
    return (
      <Badge className="gap-1 bg-emerald-500/12 text-green-800 dark:text-green-200 border-0">
        <CheckCircle2 className="h-3.5 w-3.5" />
        Connected
      </Badge>
    )
  }
  if (status === 'reconnect_needed') {
    return (
      <Badge className="gap-1 bg-amber-500/12 text-yellow-800 dark:text-yellow-200 border-0">
        <AlertTriangle className="h-3.5 w-3.5" />
        Reconnect Needed
      </Badge>
    )
  }
  return (
    <Badge variant="secondary" className="gap-1">
      Not Connected
    </Badge>
  )
}

// ============================================================
// Component
// ============================================================

type LinkedInConnectProps = {
  /** Called after a successful connection (e.g. to advance an onboarding wizard). */
  readonly onConnected?: () => void
}

export default function LinkedInConnect({ onConnected }: LinkedInConnectProps = {}) {
  const apiFetch = useApi()
  const queryClient = useQueryClient()
  const [status, setStatus] = useState<ConnectionStatus>('not_connected')
  const [account, setAccount] = useState<ConnectedAccount | null>(null)
  const [isConnecting, setIsConnecting] = useState(false)
  const [isDisconnecting, setIsDisconnecting] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Load initial status on mount
  useEffect(() => {
    void loadStatus()
    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, []) // intentionally empty: run once on mount

  async function loadStatus(): Promise<void> {
    try {
      const data = await fetchLinkedInStatus(apiFetch)
      applyStatus(data)
    } catch {
      // Ignore — status stays at not_connected
    }
  }

  function applyStatus(data: StatusResponse): void {
    setStatus(data.status)
    if (data.status === 'connected' && data.account) {
      setAccount({
        name: data.account.name,
        connectedAt: parseApiDate(data.account.connected_at) ?? new Date(),
      })
    }
  }

  function stopPolling(): void {
    if (pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
  }

  async function handleConnect() {
    setIsConnecting(true)
    setError(null)
    try {
      // 1. Get the Unipile hosted-auth URL from the backend
      const result = await initiateLinkedInConnect(apiFetch)

      if (!result.success || !result.auth_url) {
        setError(result.error ?? 'Failed to start LinkedIn connection. Please try again.')
        setIsConnecting(false)
        return
      }

      // 2. Open the OAuth URL in a popup window
      const popup = window.open(
        result.auth_url,
        'linkedin-oauth',
        'width=600,height=700,left=200,top=100,resizable=yes,scrollbars=yes'
      )

      if (!popup) {
        setError('Popup blocked — please allow popups for this site and try again.')
        setIsConnecting(false)
        return
      }

      // 3. Poll /status every 2 seconds until connected or popup closes
      pollRef.current = setInterval(async () => {
        if (popup.closed) {
          stopPolling()
          setIsConnecting(false)
          return
        }

        try {
          const data = await fetchLinkedInStatus(apiFetch)
          if (data.status === 'connected') {
            stopPolling()
            popup.close()
            applyStatus(data)
            setIsConnecting(false)
            onConnected?.()
          }
        } catch {
          // Keep polling — transient error
        }
      }, 2000)

      // 4. Stop polling after 10 minutes regardless
      setTimeout(() => {
        stopPolling()
        setIsConnecting(false)
      }, 10 * 60 * 1000)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to start LinkedIn connection'
      setError(message)
      setIsConnecting(false)
    }
  }

  async function handleDisconnect() {
    setError(null)
    setIsDisconnecting(true)
    stopPolling()

    const result = await performLinkedInDisconnect(apiFetch)

    if (!result.success) {
      setError(result.error ?? 'Failed to disconnect LinkedIn. Please try again.')
      setIsDisconnecting(false)
      // Don't clear UI — backend state is unchanged, so leaving "Connected"
      // visible reflects reality.
      return
    }

    // Refetch from server to confirm the new state. Don't trust optimistic
    // local state — the entire point of this fix is to keep UI in sync with
    // the DB.
    try {
      const fresh = await fetchLinkedInStatus(apiFetch)
      applyStatus(fresh)
      // Reset cached account if the server says we're no longer connected
      if (fresh.status !== 'connected') setAccount(null)
    } catch {
      // If status fetch fails, fall back to optimistic clear — we know the
      // backend ran disconnect successfully.
      setStatus('not_connected')
      setAccount(null)
    }

    // Knock out every other query in the app that reads LinkedIn state
    // (Add Leads, Content publish, Inbox reply, etc.). They'll all refetch
    // and converge on the same source of truth.
    await queryClient.invalidateQueries({ queryKey: ['linkedin'] })

    setConfirmOpen(false)
    setIsDisconnecting(false)
  }

  return (
    <div className="space-y-4">
      {/* Main connection card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Linkedin className="h-5 w-5 text-[#0077b5]" />
              <CardTitle>LinkedIn Account</CardTitle>
            </div>
            <StatusBadge status={status} />
          </div>
          <CardDescription>
            Connect your LinkedIn account to enable automated outreach and content posting.
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {error && (
            <p className="text-sm text-red-300 dark:text-red-400 flex items-center gap-1.5">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              {error}
            </p>
          )}

          {status === 'connected' && account ? (
            <div className="flex items-center gap-4 rounded-lg border border-emerald-500/25 bg-emerald-500/10 dark:border-green-800 p-4">
              {/* Avatar placeholder */}
              <div className="h-12 w-12 rounded-full bg-gradient-to-br from-[#0077b5] to-[#00a0dc] flex items-center justify-center text-white font-semibold text-lg">
                {account.name.charAt(0)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{account.name}</p>
                <p className="text-xs text-muted-foreground">
                  Connected {formatDate(account.connectedAt)}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setConfirmOpen(true)}
                disabled={isDisconnecting}
              >
                {isDisconnecting ? (
                  <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Disconnecting...</>
                ) : (
                  'Disconnect'
                )}
              </Button>
            </div>
          ) : (
            <div className="flex flex-col items-start gap-3">
              {status === 'reconnect_needed' && (
                <p className="text-sm text-amber-300 flex items-center gap-1.5">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  Your LinkedIn session expired. Please reconnect to resume automation.
                </p>
              )}
              <Button
                onClick={() => void handleConnect()}
                disabled={isConnecting}
                className="gap-2"
              >
                {isConnecting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Waiting for authorization…
                  </>
                ) : (
                  <>
                    <Linkedin className="h-4 w-4" />
                    {status === 'reconnect_needed' ? 'Reconnect LinkedIn' : 'Connect LinkedIn'}
                  </>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info card */}
      <Card className="border-dashed">
        <CardContent className="pt-4">
          <div className="flex gap-3">
            <Info className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
            <div className="space-y-1.5 text-sm text-muted-foreground">
              <p className="font-medium text-foreground">What we access</p>
              <ul className="space-y-1 list-disc list-inside">
                <li>Send connection requests on your behalf</li>
                <li>Send and receive direct messages</li>
                <li>Publish content to your LinkedIn feed</li>
                <li>View your public profile information</li>
              </ul>
              <p className="pt-1 text-xs">
                We connect to LinkedIn via secure OAuth — your credentials are never stored in plain text.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={confirmOpen} onOpenChange={(o) => !isDisconnecting && setConfirmOpen(o)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Disconnect LinkedIn?</DialogTitle>
            <DialogDescription className="text-foreground/90">
              This will disconnect your LinkedIn account, pause any active campaigns, and stop all automated outreach and content posting. You can reconnect at any time.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={isDisconnecting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={() => void handleDisconnect()} disabled={isDisconnecting}>
              {isDisconnecting ? (
                <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Disconnecting...</>
              ) : (
                'Disconnect LinkedIn'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
